import { inject, Injectable } from "@angular/core";
import { lastValueFrom } from "rxjs";
import { AttachmentBackend } from "src/app/backend/api/attachment.backend";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import {
  PdfJsWorker,
  PdfJsWorkerService
} from "src/app/services/pdfjs-worker.service";
import { isUrl } from "src/app/support/url";

declare const pdfjsLib: {
  getDocument: (params: { url: string; worker: PdfJsWorker }) => {
    promise: Promise<PdfJsDocument>;
  };
};

type PdfJsDocument = {
  getPage: (pageNumber: number) => Promise<PdfJsPage>;
  destroy: () => Promise<void>;
};

type PdfJsPage = {
  getViewport: (params: {
    scale: number;
    rotation: number;
    dontFlip: boolean;
  }) => { width: number; height: number };
  render: (params: {
    canvasContext: CanvasRenderingContext2D;
    viewport: { width: number; height: number };
  }) => { promise: Promise<void> };
};

export type PdfThumbnailParams = {
  storageId: string;
  isAttachmentId?: boolean;
};

const THUMBNAIL_WIDTH_PX = 164;
const THUMBNAIL_ASPECT_RATIO = 1.4142;

@Injectable({
  providedIn: "root"
})
export class PdfThumbnailService {
  readonly #attachmentBackend = inject(AttachmentBackend);
  readonly #storageBackend = inject(StorageBackend);
  readonly #pdfJsWorkerService = inject(PdfJsWorkerService);

  readonly #cache = new Map<string, Blob>();
  readonly #inflight = new Map<string, Promise<Blob>>();

  #queue: Promise<void> = Promise.resolve();

  clearCache(storageId?: string): void {
    if (storageId) {
      this.#cache.delete(this.buildCacheKey({ storageId }));

      return;
    }

    this.#cache.clear();
  }

  buildCacheKey(params: PdfThumbnailParams): string {
    if (params.isAttachmentId) {
      return `attachment:${params.storageId}`;
    }

    return params.storageId;
  }

  generateThumbnail(params: PdfThumbnailParams): Promise<Blob> {
    const cacheKey = this.buildCacheKey(params);
    const cached = this.#cache.get(cacheKey);

    if (cached) {
      return Promise.resolve(cached);
    }

    const inflight = this.#inflight.get(cacheKey);

    if (inflight) {
      return inflight;
    }

    const promise = this.#enqueue(() => this.#renderThumbnail(params))
      .then((blob) => {
        this.#cache.set(cacheKey, blob);
        this.#inflight.delete(cacheKey);

        return blob;
      })
      .catch((error: unknown) => {
        this.#inflight.delete(cacheKey);

        throw error;
      });

    this.#inflight.set(cacheKey, promise);

    return promise;
  }

  #enqueue<T>(job: () => Promise<T>): Promise<T> {
    const result = this.#queue.then(() => job());
    this.#queue = result.then(
      (): void => undefined,
      (): void => undefined
    );

    return result;
  }

  async #renderThumbnail(params: PdfThumbnailParams): Promise<Blob> {
    const worker = await this.#pdfJsWorkerService.getWorker();

    const pdfUrl = await this.#resolvePdfUrl(params);
    const pdfDocument = await pdfjsLib.getDocument({ url: pdfUrl, worker })
      .promise;

    try {
      const page = await pdfDocument.getPage(1);
      const canvas = await this.#renderPageToCanvas(page);

      return await this.#canvasToBlob(canvas);
    } finally {
      await pdfDocument.destroy();
    }
  }

  async #resolvePdfUrl(params: PdfThumbnailParams): Promise<string> {
    if (isUrl(params.storageId)) {
      return params.storageId;
    }

    if (params.isAttachmentId) {
      const attachment = await lastValueFrom(
        this.#attachmentBackend.load(params.storageId)
      );

      return lastValueFrom(this.#storageBackend.download(attachment.storageId));
    }

    return lastValueFrom(this.#storageBackend.download(params.storageId));
  }

  async #renderPageToCanvas(page: PdfJsPage): Promise<HTMLCanvasElement> {
    const viewport = page.getViewport({
      scale: 1,
      rotation: 0,
      dontFlip: false
    });
    const canvas = document.createElement("canvas");
    canvas.width = THUMBNAIL_WIDTH_PX;
    canvas.height = THUMBNAIL_WIDTH_PX * THUMBNAIL_ASPECT_RATIO;

    const scale = Math.min(
      canvas.width / viewport.width,
      canvas.height / viewport.height
    );
    const scaledViewport = page.getViewport({
      scale,
      rotation: 0,
      dontFlip: false
    });
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("PDF thumbnail canvas context unavailable");
    }

    await page.render({
      canvasContext: context,
      viewport: scaledViewport
    }).promise;

    return canvas;
  }

  #canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);

          return;
        }

        reject(new Error("PDF thumbnail blob conversion failed"));
      }, "image/png");
    });
  }
}
